import React, {useState, useEffect} from 'react';
import {BaseCRUDPage} from '../common';
import {Input, RequiredIndicator, Column, FieldError, SearchableSelect} from '../../components';
import {modelService, makeService} from '../../services';
import {Model, ModelCreate, ModelUpdate, Make} from '../../types';

interface ModelFormData {
    code: string;
    name: string;
    type: string;
    weight: string;
    length: string;
    width: string;
    height: string;
    cbm: string;
    make_id: string;
}

const ModelsPage: React.FC = () => {
    const [makes, setMakes] = useState<Make[]>([]);
    const [formData, setFormData] = useState<ModelFormData>({
        code: '',
        name: '',
        type: '',
        weight: '',
        length: '',
        width: '',
        height: '',
        cbm: '',
        make_id: ''
    });

    // Load makes for dropdown
    useEffect(() => {
        const loadMakes = async () => {
            try {
                const result = await makeService.getAll({limit: 1000});
                setMakes(result);
            } catch (error) {
                console.error('Error loading makes:', error);
            }
        };
        loadMakes();
    }, []);

    // Define table columns
    const columns: Column[] = [
        {key: 'code', label: 'Code', sortable: true, searchable: true},
        {
            key: 'make_id',
            label: 'Make',
            sortable: true,
            searchable: true,
            render: (value: string) => {
                const make = makes.find(m => m.id === value);
                return make ? make.name : '-';
            }
        },
        {key: 'name', label: 'Name', sortable: true, searchable: true},
        {key: 'type', label: 'Type', sortable: true, searchable: true},
        {key: 'weight', label: 'Weight (kg)', sortable: true},
        {key: 'length', label: 'Length (m)', sortable: true},
        {key: 'width', label: 'Width (m)', sortable: true},
        {key: 'height', label: 'Height (m)', sortable: true},
        {key: 'cbm', label: 'CBM', sortable: true}
    ];

    // Calculate CBM from dimensions
    const calculateCBM = (length: string, width: string, height: string): string => {
        const l = parseFloat(length) || 0;
        const w = parseFloat(width) || 0;
        const h = parseFloat(height) || 0;
        if (l > 0 && w > 0 && h > 0) {
            return (l * w * h).toFixed(2);
        }
        return '';
    };

    // Handle dimension change with auto CBM calculation
    const handleDimensionChange = (field: 'length' | 'width' | 'height', value: string) => {
        setFormData(prev => {
            const newData = {...prev, [field]: value};
            newData.cbm = calculateCBM(newData.length, newData.width, newData.height);
            return newData;
        });
    };

    // Form fields component with error support
    const formFields = (fieldErrors: Record<string, string>) => (
        <div className="space-y-3">
            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Code <RequiredIndicator/>
                    </label>
                    <Input
                        type="text"
                        value={formData.code}
                        onChange={(e) => setFormData(prev => ({...prev, code: e.target.value}))}
                        required
                        placeholder="Enter model code"
                    />
                    <FieldError error={fieldErrors.code}/>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Name <RequiredIndicator/>
                    </label>
                    <Input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                        required
                        placeholder="Enter model name"
                    />
                    <FieldError error={fieldErrors.name}/>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Type <RequiredIndicator/>
                    </label>
                    <SearchableSelect
                        options={[
                            {value: 'B', label: 'B'},
                            {value: 'P', label: 'P'},
                            {value: 'G', label: 'G'},
                            {value: 'L', label: 'L'}
                        ]}
                        value={formData.type}
                        onChange={(value) => setFormData(prev => ({...prev, type: value}))}
                        placeholder="Select a type"
                    />
                    <FieldError error={fieldErrors.type}/>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Make <RequiredIndicator/>
                    </label>
                    <SearchableSelect
                        options={makes.map(make => ({
                            value: make.id,
                            label: `${make.code} - ${make.name}`
                        }))}
                        value={formData.make_id}
                        onChange={(value) => setFormData(prev => ({...prev, make_id: value}))}
                        placeholder="Select a make"
                    />
                </div>
            </div>

            {/* Dimensions */}
            <div className="grid grid-cols-3 gap-3">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Length (m) <RequiredIndicator/>
                    </label>
                    <Input
                        type="number"
                        step="0.01"
                        value={formData.length}
                        onChange={(e) => handleDimensionChange('length', e.target.value)}
                        required
                        placeholder="Enter length"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Width (m) <RequiredIndicator/>
                    </label>
                    <Input
                        type="number"
                        step="0.01"
                        value={formData.width}
                        onChange={(e) => handleDimensionChange('width', e.target.value)}
                        required
                        placeholder="Enter width"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Height (m) <RequiredIndicator/>
                    </label>
                    <Input
                        type="number"
                        step="0.01"
                        value={formData.height}
                        onChange={(e) => handleDimensionChange('height', e.target.value)}
                        required
                        placeholder="Enter height"
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Weight (kg) <RequiredIndicator/>
                    </label>
                    <Input
                        type="number"
                        step="0.01"
                        value={formData.weight}
                        onChange={(e) => setFormData(prev => ({...prev, weight: e.target.value}))}
                        required
                        placeholder="Enter weight"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        CBM (m³) <span
                        className="text-xs text-gray-500 dark:text-gray-400 font-normal">(auto-calculated)</span>
                    </label>
                    <Input
                        type="number"
                        step="0.01"
                        value={formData.cbm}
                        onChange={() => {
                        }}
                        disabled
                        placeholder="L × W × H"
                    />
                </div>
            </div>
        </div>
    );

    // Modal icon
    const modalIcon = (
        <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor"
                 viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
        </div>
    );

    return (
        <BaseCRUDPage<Model, ModelCreate, ModelUpdate>
            title="Model Management"
            entityName="model"
            columns={columns}
            service={modelService}
            formFields={formFields}
            formData={formData}
            setFormData={setFormData}
            requiredFields={['code', 'name', 'type', 'weight', 'cbm', 'length', 'width', 'height', 'make_id']}
            getInitialFormData={() => ({
                code: '',
                name: '',
                type: '',
                weight: '',
                length: '',
                width: '',
                height: '',
                cbm: '',
                make_id: ''
            })}
            toCreateData={(formData) => ({
                code: formData.code,
                name: formData.name,
                type: formData.type,
                weight: parseFloat(formData.weight),
                length: parseFloat(formData.length),
                width: parseFloat(formData.width),
                height: parseFloat(formData.height),
                cbm: parseFloat(formData.cbm),
                make_id: formData.make_id
            })}
            toUpdateData={(formData, original) => {
                const updateData: ModelUpdate = {};
                if (formData.code !== original.code) updateData.code = formData.code;
                if (formData.name !== original.name) updateData.name = formData.name;
                if (formData.type !== original.type) updateData.type = formData.type;
                if (parseFloat(formData.weight) !== original.weight) updateData.weight = parseFloat(formData.weight);
                if (parseFloat(formData.length) !== original.length) updateData.length = parseFloat(formData.length);
                if (parseFloat(formData.width) !== original.width) updateData.width = parseFloat(formData.width);
                if (parseFloat(formData.height) !== original.height) updateData.height = parseFloat(formData.height);
                if (parseFloat(formData.cbm) !== original.cbm) updateData.cbm = parseFloat(formData.cbm);
                return updateData;
            }}
            toFormData={(model) => ({
                code: model.code,
                name: model.name,
                type: model.type,
                weight: model.weight.toString(),
                length: model.length.toString(),
                width: model.width.toString(),
                height: model.height.toString(),
                cbm: model.cbm.toString(),
                make_id: model.make_id
            })}
            modalIcon={modalIcon}
            modalTitle={(isEdit) => ({
                title: isEdit ? 'Edit Model' : 'Create New Model',
                subtitle: isEdit ? 'Update model specifications' : 'Add a new model with detailed specifications'
            })}
            customDeleteConfirm={(model) => `Are you sure you want to delete model "${model.name}"?`}
            defaultSearchColumn="code"
        />
    );
};

export default ModelsPage;
